import { PortalEventContext } from '@app/core/models/portal-event-context';
import {
  EventSourceService,
  PortalEventData,
  PortalEventSubject,
} from '@app/core/services/event-source.service';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { RouteDataContext } from '@app/routing/models/current-route-data';
import { RouterService } from '@app/routing/router.service';
import { Cached, CachedRememberingEntry } from '@app/utils/cached/cached';
import { loggerFor } from '@app/utils/logger';
import produce from 'immer';
import {
  combineLatest,
  Observable,
  ReplaySubject,
  Subscribable,
  Subscription,
  switchMap,
  Unsubscribable,
} from 'rxjs';
import { Observer } from 'rxjs/internal/types';
import { debounceTime, distinctUntilChanged, first, map } from 'rxjs/operators';

export class CachedEventSourceConnector<T> implements Subscribable<PortalEventData> {
  private contexts: PortalEventContext[] = [];
  private eventDataSubject = new ReplaySubject<PortalEventData>(1);
  private eventSourceSubscription: Subscription;
  private log = loggerFor('CachedEventSourceConnector');
  private rememberingMap = new Map<string, EntryToRemember>();

  constructor(
    private eventSourceService: EventSourceService,
    private routerService: RouterService,
    private portalDataService: PortalDataService,
    private options: CachedEventSourceConnectorOptions<T>
  ) {
    if (Array.isArray(options.context)) {
      this.contexts = options.context;
    } else {
      this.contexts = [options.context];
    }

    this.initialise();
  }

  stop(): void {
    this.eventSourceSubscription?.unsubscribe();
  }

  subscribe(
    subscriber: Partial<Observer<PortalEventData>> | ((value: PortalEventData) => void)
  ): Unsubscribable {
    return this.eventDataSubject.subscribe(subscriber);
  }

  private addSubscriptions(entries: EntryToRemember[], system: string): Observable<PortalEventData> {
    const allSubjects = this.contexts.flatMap(
      (context) =>
        entries?.map((entry): PortalEventSubject => {
          return {
            context,
            endUser: entry.routeDataContext.userId,
            enterprise: entry.routeDataContext.enterpriseId,
            serviceProvider: entry.routeDataContext.serviceProviderId,
            system,
          };
        }) ?? []
    );

    return this.eventSourceService.addSubjects(...allSubjects);
  }

  private calculateSubjects(
    remembering: CachedRememberingEntry[],
    context: RouteDataContext
  ): EntryToRemember[] {
    this.log.debug('Remembering updated', remembering, context);

    for (const key of this.rememberingMap.keys()) {
      if (remembering.some((entry) => entry.id === key) === false) {
        this.rememberingMap.delete(key);
      } else {
        const updatedEntry = produce(this.rememberingMap.get(key), (draft) => {
          draft.entry.active = false;
        });
        this.rememberingMap.set(key, updatedEntry);
      }
    }

    const active = remembering.find((entry) => entry.active);
    const menuDataMatches = this.options.calculateId(context) === active?.id;

    if (menuDataMatches) {
      this.rememberingMap.set(active.id, { entry: active, routeDataContext: context });
      this.log.debug(
        'Remembering: ',
        Array.from(this.rememberingMap.entries()).map(([key, value]) => `${key}: ${value.entry.active}`)
      );
    }

    return Array.from(this.rememberingMap.values());
  }

  private compareRememberedEntries(a: EntryToRemember[], b: EntryToRemember[]): boolean {
    const comparableA = a
      .map(({ routeDataContext }) => JSON.stringify(routeDataContext))
      .sort()
      .join(';');

    const comparableB = b
      .map(({ routeDataContext }) => JSON.stringify(routeDataContext))
      .sort()
      .join(';');

    return comparableA === comparableB;
  }

  private initialise(): void {
    let subscription: Subscription | null = null;

    this.options.cached.hasSubscriptions$.subscribe((hasSubscriptions) => {
      if (hasSubscriptions) {
        subscription = this.portalDataService.appInfo.data$.pipe(first()).subscribe((appInfo) => {
          this.subscribeToEventSource(appInfo.system);
        });
      } else {
        subscription?.unsubscribe();
        subscription = null;

        this.eventSourceSubscription?.unsubscribe();
        this.eventSourceSubscription = null;
      }
    });
  }

  private subscribeToCurrent(system: string): void {
    this.eventSourceSubscription = this.routerService.currentRouteData$
      .pipe(
        switchMap(({ context: { userId, enterpriseId, serviceProviderId } }) => {
          const subjects = this.contexts.map((context): PortalEventSubject => {
            return {
              context,
              endUser: userId,
              enterprise: enterpriseId,
              serviceProvider: serviceProviderId,
              system,
            };
          });

          return this.eventSourceService.addSubjects(...subjects);
        })
      )
      .subscribe({
        next: (value) => {
          this.eventDataSubject.next(value);
        },
      });
  }

  private subscribeToEventSource(system: string): void {
    if (this.eventSourceSubscription != null) {
      this.eventSourceSubscription.unsubscribe();
    }

    if (this.options.watchRemembered) {
      if (this.options.watchRemembered && this.options.cached.remembering$ != null) {
        this.subscribeWithRememberingSupport(system);
      }
    } else {
      this.subscribeToCurrent(system);
    }
  }

  private subscribeWithRememberingSupport(system: string): void {
    this.eventSourceSubscription = combineLatest([
      this.options.cached.remembering$,
      this.routerService.currentRouteData$,
    ])
      .pipe(
        map(([remembering, routeData]) => this.calculateSubjects(remembering, routeData.context)),
        debounceTime(500),
        distinctUntilChanged((a, b) => this.compareRememberedEntries(a, b)),
        switchMap((entries) => this.addSubscriptions(entries, system))
      )
      .subscribe({
        next: (value) => {
          // Check if there is a remembered value for the current
          // route. It's okay if it doesn't exist, this can happen,
          // e.g. if an event is received while a cached instance is
          // being refreshed (which will remove the remembered entry).
          const rememberedValue = Array.from(this.rememberingMap.values()).find(({ routeDataContext }) => {
            return (
              routeDataContext.serviceProviderId === value.subject.serviceProvider &&
              routeDataContext.enterpriseId === value.subject.enterprise &&
              routeDataContext.userId === value.subject.endUser
            );
          });

          if (rememberedValue == null) {
            return;
          }

          if (rememberedValue.entry.active) {
            this.eventDataSubject.next(value);
          } else {
            this.options.cached.clearRememberedValue(rememberedValue.entry.id);
          }
        },
      });
  }
}

export interface CachedEventSourceConnectorOptions<T> {
  /**
   * The cached instance to connect to.
   */
  cached: Cached<T>;

  /**
   * A function that is used to map received subjects from the
   * event source to remembered entries of cached instances. This
   * function is only necessary if {@link watchRemembered} is true.
   *
   * @param routeDataContext An object containing relevant route data that is normally used to create cached IDs.
   */
  calculateId?: (routeDataContext: RouteDataContext) => string;

  /**
   * One or more contexts that the connector will subscribe to.
   */
  context: PortalEventContext | PortalEventContext[];

  /**
   * If true, this will subscribe to currently remembered entries as well.
   * Default is false.
   */
  watchRemembered?: boolean;
}

interface EntryToRemember {
  entry: CachedRememberingEntry;
  routeDataContext: RouteDataContext;
}
